import { api, FetchMeDocument } from '@mbfab/client-api'
import { isNull, isSome } from '@mbfab/shared'
import { createFileRoute } from '@tanstack/react-router'
import { uniq } from 'es-toolkit'
import { useState } from 'react'

import { AppSidebarContainer } from '@/components/app/app-sidebar-container'
import { GraphqlErrorPanel } from '@/components/app/graphql-error-panel'
import type { MillsheetSortConfigState } from '@/components/screens/organization-settings-workspace'
import { OrganizationSettingsWorkspace } from '@/components/screens/organization-settings-workspace'
import { classifyGraphqlError } from '@/lib/apollo/error-classification'
import { userFacingErrorMessage } from '@/lib/user-facing-error-message'
import { useMe } from '@/providers/me-context'

// v1 の routes/t/$tenant_id/setting.tsx と URL を合わせる（旧 organization-settings.tsx から移動）
export const Route = createFileRoute('/t/$tenant_id/setting')({
  component: OrganizationSettingsPage,
})

function OrganizationSettingsPage() {
  const { tenant_id: tenantId } = Route.useParams()
  return (
    <div className="flex min-h-screen">
      <AppSidebarContainer tenantId={tenantId} />
      <main className="min-w-0 flex-1">
        <SettingContainer />
      </main>
    </div>
  )
}

// fetchMillsheetUniqueValues の返り値キー（複数形）と、設定のカテゴリキー（単数形）の対応
const categoryUniqueKey = {
  manufacturerName: 'manufacturerNames',
  steelType: 'steelTypes',
  sectionType: 'sectionTypes',
} as const

function SettingContainer() {
  const me = useMe()
  const client = api.useClient()
  const uniqueValuesQuery = api.useFetchMillsheetUniqueValuesQuery()
  const [saving, setSaving] = useState(false)

  // エラー表示は「まだ一度も取得できていない」ときだけにする（users.tsx と同じ考え方）
  if (isNull(uniqueValuesQuery.data) && isSome(uniqueValuesQuery.error)) {
    // 組織設定の変更（updateTenantSetting）は manager 以上限定。member は親 route（isManagerOnlyPath）で止める。
    // 候補の取得（fetchMillsheetUniqueValues）は member も使えるが、テナント停止などで拒否されたときの表示として残す
    if (classifyGraphqlError(uniqueValuesQuery.error) === 'forbidden') {
      return (
        <GraphqlErrorPanel
          layout="section"
          classification="forbidden"
          subject="組織設定"
          description="設定の変更は管理者のみが行えます。組織の管理者にご依頼ください。"
          onRetry={() => {
            void uniqueValuesQuery.refetch()
          }}
        />
      )
    }
    return (
      <GraphqlErrorPanel
        layout="section"
        error={uniqueValuesQuery.error}
        subject="組織設定"
        onRetry={() => {
          void uniqueValuesQuery.refetch()
        }}
      />
    )
  }

  // me から取れる値なので、uniqueValuesQuery の初回ロードを待たずに組み立てられる
  // （画面まるごとの CenteredLoading をやめる。2026/08 レビューFB対応）
  const sortConfig = me.tenant.setting?.millsheetSortConfig
  const initialConfig: MillsheetSortConfigState = {
    manufacturerName: uniq(sortConfig?.manufacturerName?.filter(isSome) ?? []),
    steelType: uniq(sortConfig?.steelType?.filter(isSome) ?? []),
    sectionType: uniq(sortConfig?.sectionType?.filter(isSome) ?? []),
  }

  // 初回ロード中は「データに存在する未設定候補」だけが空のまま表示され、取得完了後に追従する
  // （設定済み一覧は initialConfig 由来なので即座に表示できる。OrganizationSettingsWorkspace は
  // uniqueValues を state に取り込まず毎レンダー参照するため、後から埋まっても再同期の問題は起きない）
  const millsheetUniqueValues = uniqueValuesQuery.data?.millsheetUniqueValues
  const uniqueValues: MillsheetSortConfigState = {
    manufacturerName: millsheetUniqueValues?.[categoryUniqueKey.manufacturerName] ?? [],
    steelType: millsheetUniqueValues?.[categoryUniqueKey.steelType] ?? [],
    sectionType: millsheetUniqueValues?.[categoryUniqueKey.sectionType] ?? [],
  }

  async function handleSave(config: MillsheetSortConfigState): Promise<{ ok: true } | { ok: false; message: string }> {
    setSaving(true)
    try {
      // refetchQueries は awaitRefetchQueries を指定しないため（既定 false）、この呼び出しの
      // reject は保存（mutation本体）の失敗のみを表す。refetch自体の失敗はこの catch に伝播せず、
      // 「保存に失敗した」と誤って報告することはない（PR B の users.tsx の runMutation と同じ考え方）
      await client.updateTenantSetting(
        { input: { millsheetSortConfig: config } },
        { refetchQueries: [FetchMeDocument] },
      )
    } catch (error) {
      return { ok: false, message: userFacingErrorMessage(error, '設定の保存に失敗しました') }
    } finally {
      setSaving(false)
    }
    return { ok: true }
  }

  return (
    <OrganizationSettingsWorkspace
      initialConfig={initialConfig}
      uniqueValues={uniqueValues}
      saving={saving}
      onSave={handleSave}
    />
  )
}
